/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * Whether a real browser-history entry exists behind the current one — i.e.
 * whether `navigate(-1)` would actually land somewhere instead of doing
 * nothing.
 *
 * Browser history is the authority here, not the Redux navigation stack. The
 * stack tracks REPLACE separately (see `replaceNavigation`) so the two agree in
 * the common case, but it is a bounded mirror — capped at
 * `NAVIGATION_MAX_STACK_SIZE`, and `popNavigation` refuses to empty itself — so
 * it can still report a depth the real history does not have. A pop that goes
 * nowhere is silent: the route simply does not change, which on a TV leaves the
 * user stranded with no way off the screen.
 *
 * `react-router`'s history keeps a monotonic entry index in
 * `history.state.idx` and uses it to answer exactly this question, so read
 * that. It is written during `createBrowserRouter()` initialisation, so it is
 * present for the whole life of the app; when it is absent (a non-browser
 * history — `MemoryRouter` under test — or a host without `history.state`)
 * there is no way to know, and this reports `false` so callers route to a
 * known-good destination rather than issuing a pop that may go nowhere.
 */
export function canNavigateBackInHistory(): boolean {
  if (typeof window === "undefined" || !window.history) {
    return false;
  }

  const state: unknown = window.history.state;
  if (state === null || typeof state !== "object") {
    return false;
  }

  const index: unknown = (state as Record<string, unknown>).idx;
  if (typeof index !== "number") {
    return false;
  }

  return index > 0;
}
